import React, { useState, useEffect } from 'react';
import { getPhotos, uploadPhoto, deletePhoto } from '../services/apiService';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { 
  Plus, 
  Trash2, 
  Upload, 
  X, 
  Image as ImageIcon, 
  Loader2 
} from 'lucide-react';

const AdminPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { toast, showToast, hideToast } = useToast();
  
  const [uploadData, setUploadData] = useState({
    file: null,
    category: 'gallery',
    title: '',
  });

  useEffect(() => {
    loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const loadPhotos = async () => {
    try {
      setIsLoading(true);
      const data = await getPhotos(selectedCategory);
      setPhotos(data);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file: file });
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      showToast('Please select a file', 'error');
      return;
    }

    try {
      setUploading(true);
      await uploadPhoto(uploadData);
      handleCloseModal();
      loadPhotos();
      showToast('Photo uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading photo:', error);
      showToast('Error uploading photo. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this photo? This cannot be undone.')) {
      try {
        await deletePhoto(id);
        loadPhotos();
        showToast('Photo deleted successfully!', 'success');
      } catch (error) {
        console.error('Error deleting photo:', error);
        showToast('Error deleting photo. Please try again.', 'error');
      }
    }
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setUploadData({ file: null, category: 'gallery', title: '' });
    setPreviewUrl(null);
  };

  const categories = [
    { id: 'all', name: 'All Photos' },
    { id: 'visa', name: 'Visa Success' },
    { id: 'gallery', name: 'Gallery' },
    { id: 'events', name: 'Events' },
    { id: 'training', name: 'Training' },
    { id: 'students', name: 'Students' },
    { id: 'university', name: 'Logos' },
  ];

  return (
    <div className="space-y-6 md:space-y-8">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Photo Gallery</h1>
            <p className="text-slate-500 text-xs md:text-sm mt-1">Manage images across your website.</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 md:px-5 md:py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 w-full md:w-auto justify-center"
        >
          <Plus size={18} />
          Upload New
        </button>
      </div>

      {/* FILTER TABS */}
      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max">
            {categories.map((cat) => (
            <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap ${
                selectedCategory === cat.id
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
            >
                {cat.name}
            </button>
            ))}
        </div>
      </div>

      {/* PHOTOS GRID */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-red-600 mb-4"></div>
          <p className="text-slate-500">Loading gallery...</p>
        </div>
      ) : photos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {photos.map((photo) => (
            <div key={photo.id} className="group bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all overflow-hidden relative">
              
              {/* Image Aspect Ratio Container */}
              <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
                {(() => {
                  const url = photo.url || photo.photo_url || photo.imageUrl;
                  const safeUrl = url && !url.startsWith('blob:') ? url : null;
                  return safeUrl ? (
                    <img
                      src={safeUrl}
                      alt={photo.title || 'Gallery Image'}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null;
                })()}
                <div className="hidden w-full h-full items-center justify-center bg-slate-200 text-slate-400 text-xs">
                  Invalid Image
                </div>
                
                {/* Overlay Action - Mobile Optimized: Visible on tap or hover */}
                <div className="absolute top-2 right-2 md:inset-0 md:bg-black/40 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                        onClick={() => handleDelete(photo.id)}
                        className="bg-red-600 text-white p-1.5 md:p-2 rounded-full hover:bg-red-700 transition-colors shadow-lg md:transform md:scale-90 md:group-hover:scale-100"
                        title="Delete Photo"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
              </div>

              {/* Caption */}
              <div className="p-2 md:p-3">
                <p className="font-medium text-slate-800 text-xs md:text-sm truncate" title={photo.title || 'Untitled'}>
                  {photo.title || 'Untitled'}
                </p>
                <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                        {photo.category}
                    </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
            <ImageIcon size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-900">No photos found</h3>
          <p className="text-slate-500 mt-1 text-sm">Upload a photo to get started with this category.</p>
        </div>
      )}

      {/* UPLOAD MODAL */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom md:zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 sticky top-0 z-10 backdrop-blur-md">
              <h2 className="text-lg font-bold text-slate-900">Upload Photo</h2>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-red-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleUpload} className="p-6 space-y-5">
              
              {/* File Drop Area */}
              <div className="relative">
                <label className={`flex flex-col items-center justify-center w-full h-40 md:h-48 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${previewUrl ? 'border-red-200 bg-red-50/30' : 'border-slate-300 hover:border-red-400 hover:bg-slate-50'}`}>
                    {previewUrl ? (
                        <img src={previewUrl} alt="Preview" className="w-full h-full object-contain rounded-lg p-2" />
                    ) : (
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload size={32} className="text-slate-400 mb-2" />
                            <p className="text-sm text-slate-500 font-medium text-center">Click to upload image</p>
                            <p className="text-xs text-slate-400 mt-1">PNG, JPG up to 5MB</p>
                        </div>
                    )}
                    <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                </label>
              </div>

              {/* Inputs */}
              <div className="space-y-4">
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Title</label>
                    <input
                        type="text"
                        value={uploadData.title}
                        onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                        placeholder="e.g. Graduation Ceremony 2024"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Category</label>
                    <select
                        value={uploadData.category}
                        onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm appearance-none"
                    >
                        {categories.filter(c => c.id !== 'all').map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-2 flex gap-3 pb-safe">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={uploading}
                  className="flex-1 py-2.5 rounded-lg font-medium text-slate-600 hover:bg-slate-100 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadData.file}
                  className="flex-1 bg-red-600 text-white py-2.5 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm flex items-center justify-center gap-2"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : 'Upload Photo'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default AdminPhotos;